import { useState, useEffect } from "react";
import { BellRing, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePushNotifications } from "@/hooks/usePushNotifications";

const DISMISS_KEY = "rebuilt_push_prompt_dismissed_until";
const DISMISS_DAYS = 7;

/**
 * PushPermissionBanner — a branded, contextual opt-in banner.
 *
 * Strategy:
 *   - Only render when Notification.permission === 'default' (never-asked).
 *   - Respect a 7-day "not now" cool-off stored in localStorage so we never
 *     nag the same user repeatedly after a dismiss.
 *   - Enable button triggers requestPermission() AND immediately subscribes to
 *     the web-push service. On denial, flip to a helpful row with "open browser
 *     settings" hint.
 *
 * Place this on the first screen a logged-in user lands on (customer dashboard,
 * agent dashboard, admin overview).
 */
export default function PushPermissionBanner() {
  const { requestPermission, subscribeToPush } = usePushNotifications();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof Notification === "undefined") return;
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (until && Date.now() < until) return;
    if (Notification.permission === "default") setVisible(true);
    if (Notification.permission === "denied") setDenied(false); // don't nag on denied either
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    try {
      const granted = await requestPermission();
      if (!granted) {
        setDenied(true);
        toast.error("Notifications blocked. You can re-enable them from your browser's site settings.");
        return;
      }
      const sub = await subscribeToPush();
      if (sub.success) {
        toast.success("Alerts enabled — we'll keep you posted on EMIs, approvals and payments.");
        setVisible(false);
      } else if (sub.reason === "unsupported") {
        toast.error("Your browser does not support push notifications.");
      } else if (sub.reason === "vapid-not-configured") {
        toast.error("Push not configured on server. Please contact support.");
      } else {
        toast.error(`Could not subscribe (${sub.reason}).`);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="push-permission-banner"
      className="relative overflow-hidden rounded-2xl border border-[#1565C0]/20 bg-gradient-to-r from-[#E3F2FD] via-white to-[#E3F2FD] p-4 sm:p-5 shadow-sm"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#1565C0] flex items-center justify-center shadow-sm">
          <BellRing className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm sm:text-base font-bold text-[#0A2540]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Stay on top of your EMIs
          </h3>
          <p className="mt-0.5 text-xs sm:text-sm text-[#475569] leading-relaxed">
            {denied
              ? "Notifications are blocked. Enable them from your browser's site settings to get EMI reminders."
              : "Get instant alerts for EMI reminders, application updates and payment confirmations."}
          </p>
          {!denied && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleEnable}
                disabled={busy}
                data-testid="push-enable-btn"
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#1565C0] text-white text-xs sm:text-sm font-semibold px-3.5 py-2 hover:bg-[#0D47A1] transition-colors disabled:opacity-60"
              >
                {busy ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Enabling…
                  </>
                ) : (
                  <>
                    <Check className="h-3.5 w-3.5" /> Enable alerts
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                data-testid="push-dismiss-btn"
                className="inline-flex items-center rounded-lg border border-slate-200 bg-white text-xs sm:text-sm font-medium text-[#475569] px-3.5 py-2 hover:bg-slate-50 transition-colors"
              >
                Not now
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="flex-shrink-0 text-slate-400 hover:text-slate-700 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
